<template>
  <el-dialog
    v-model="uploaderVisible"
    width="600px"
    draggable
    :before-close="onClose"
    class="file-publish-dialog file-upload-dialog"
  >
    <template #header="{ titleId, titleClass }">
      <div class="my-header">
        <h4 :id="titleId" :class="titleClass" class="mr-2">{{ t('button.uploadFiles') }}</h4>
        <el-tooltip class="box-item" effect="light" placement="top-start">
          <template #content>
            <div>
              {{ t('dialog.content.fileOne') }}<br />
              {{ t('dialog.content.fileTwo') }}<br />
              {{ t('dialog.content.fileThree') }}
            </div>
          </template>
          <CustomIcon name="notice" :font-size="20" />
        </el-tooltip>
      </div>
    </template>
    <div class="much-file-content">
      <div class="file-drag-box">
        <div class="file-label">
          <label for="file-input" class="custom-file-upload">
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <span class="el-upload__text">
              {{ t('common.uploadDrag') }}<em>{{ t('common.uploadClick') }}</em>
            </span>
          </label>
        </div>
        <input
          id="file-input"
          ref="fileInputRef"
          type="file"
          class="much-file"
          multiple
          :accept="FILE_TYPES.join(',')"
          @change="onFileChange"
          @dragenter.prevent="onDragenter"
          @dragleave.prevent="onDragleave"
        />
        <div class="file-drag-mask"></div>
      </div>
      <div v-if="files.length" class="file-list-show-box">
        <template v-for="item in files" :key="item.files.name">
          <div v-if="item.appear" class="file-list-li">
            <div class="mr-2 flex-1">
              <div class="file-list-name">
                <span>
                  <CustomIcon name="file-up" :font-size="16" v-if="item.show !== 0" />
                  <CustomIcon name="upload-begining" :font-size="16" v-else />
                </span>
                {{ item.filePath }}
              </div>
              <div>
                <el-progress :percentage="item.percentage" :color="item.color" />
              </div>
              <div>
                <span v-if="item.error" style="color: #f56c6c">{{ t(item.message) }}</span>
                <span v-if="item.success" style="color: #67c23a">{{ t(item.message) }}</span>
              </div>
            </div>
            <div class="file-li-btn ml-auto">
              <el-icon
                @click="onPauseUploading(item)"
                v-if="item.show === 0 || item.resetShow === 0"
                size="14"
                color="#67C23A"
                class="cursor-pointer"
              >
                <VideoPause />
              </el-icon>
              <el-icon
                @click="onContinueUploading(item)"
                v-if="item.show === 1 && item.resetShow === 1"
                size="14"
                color="#F56C6C"
                class="cursor-pointer"
              >
                <VideoPlay />
              </el-icon>
              <el-icon
                @click="onDeleteFile(item)"
                v-if="(item.show === 1 && item.resetShow === 1) || item.show === 4"
                size="14"
                color="#BFBFBF"
                class="cursor-pointer"
              >
                <Delete />
              </el-icon>
              <el-icon
                @click="onContinueUploading(item)"
                v-if="item.show === 2 || item.show === 4"
                size="14"
                color="#F56C6C"
                class="cursor-pointer"
              >
                <RefreshRight />
              </el-icon>
              <el-icon v-if="item.show === 3" size="14" color="#67C23A"><CircleCheck /></el-icon>
            </div>
          </div>
        </template>
      </div>
      <div class="mt-[16px]" v-else>
        <el-empty image-size="100" :description="t('dialog.upload.noData')" />
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts" setup>
/* eslint-disable no-continue */
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'

import APIS from '@/api/common'
import event from '@/utils/event'
import CustomIcon from '@/components/CustomIcon.vue'
import { useCommonStore } from '@/store/useCommonStore'
import { PUB_SUB, FILE_TYPES } from '@/static/enums'
import {
  getS3,
  abortMultipartUpload,
  createMultipartUpload,
  uploadChunk,
  completeMultipartUpload,
} from '@/components/utils/s3'

enum STATUS_COLORS {
  NORMAL = '#409EFF',
  ERROR = '#F56C6C',
  SUCCESS = '#67C23A',
}

type UploadFile = {
  fid: string
  color: STATUS_COLORS
  filePath: string
  files: File
  error: boolean
  appear: boolean
  success: boolean
  message: string
  isFresh: boolean
  show: number
  resetShow: number
  percentage: number
  s3: {
    bucket: string
    endPoint: string
    accessKeyId: string
    secretAccessKey: string
  }
}

const MAX_FILE_SIZE = 1024 * 1024 * 1024 * 4
// 定义分片的大小 为5M  采用分片上传时，只能并且只有有最后一个分片的size 小于 指定值（默认5M），不然就会报错
const CHUNK_SIZE = 5 * 1024 * 1024
const fileFolderPrefixPath = `shield_netdisc/`
const fileFolderSuffixPath = `/${moment(new Date()).format('YYYYMMDD')}/`

const { t } = useI18n()

// 获取用户编码
const store = useCommonStore()
const userId = computed(() => store.userInfo.id)

const state = reactive({
  id: null,
  rootId: null,
  folders: [],
})

// 存储上传文件列表
const files = ref<UploadFile[]>([])
const fileInputRef = ref<HTMLInputElement | null>(null)
const uploaderVisible = ref<boolean>(false)
const fileOnS3FolderPath = computed(
  () =>
    // eslint-disable-next-line prefer-template
    fileFolderPrefixPath +
    `${import.meta.env.MODE}/` +
    state.folders.join('/') +
    fileFolderSuffixPath +
    `${userId.value}_`
)

// 删除还未上传成功文件
const onDeleteFile = (item: UploadFile) => {
  ElMessageBox.confirm(t('confirm.deleteFileBeing'), t('dialog.deleteFile'), {
    confirmButtonText: t('button.deleteConfirm'),
    cancelButtonText: t('button.cancel'),
    type: 'warning',
  })
    .then(() => {
      item.appear = false
      item.show = 1
      files.value.splice(
        files.value.findIndex((fi: any) => fi.fid === item.fid),
        1
      )
      // fileInputRef.value.reset();
      const fileInput = document.getElementById('file-input') as HTMLInputElement
      if (fileInput) fileInput.value = ''
    })
    .catch(() => {})
}

// 暂停上传
const onPauseUploading = (item: UploadFile) => {
  item.show = 1
  item.error = true
  item.message = 'message.suspend'
  item.resetShow = 1
}

// 通知后端，文件上传成功 更新文件至后端
async function onPostUploadInfo(fileName: string, filePath: string, fileSuffix: string) {
  const type = fileSuffix.split('/').pop()
  await APIS.postUploadInfo({
    folderId: state.id,
    fileName,
    filePath,
    fileSuffix: type,
  })
  event.publish(PUB_SUB.LIST_STATUS)
  return true
}

// 上传文件基本信息，获取 bucket 基本信息
const fetchS3InfoAndBucketInfo = () => APIS.getFileUploadAws({ folderId: state.id })

const uploadFileWithoutChunk = (
  file: UploadFile,
  bucketName: string,
  s3: any,
  isContinued = false
) => {
  s3.upload(
    {
      Bucket: bucketName,
      Key: file.fid!,
      Body: file.files,
    },
    (err: never, data: any) => {
      if (err) return console.log(err)
      if (!isContinued && file.show === 1) return false
      if (data?.ETag) {
        if (!isContinued && file.show === 0) file.isFresh = false
        file.show = 3
        file.color = STATUS_COLORS.SUCCESS
        file.error = false
        file.success = true
        file.message = 'message.uploadSuccessful'
        file.resetShow = 3
        file.percentage = 100
        onPostUploadInfo(file.files.name, file.fid!, file.files.type)
      }
    }
  )
}

const uploadFileWithChunk = async (
  file: UploadFile,
  bucketName: string,
  s3: any,
  isContinued = false
) => {
  try {
    // 创建分片上传
    const chunkUploader = await createMultipartUpload(bucketName, file.fid!, s3, file.files.type)
    // 分片数
    const chunkCount = Math.ceil(file.files.size / CHUNK_SIZE)

    // 用来判断分片是否上传成功
    let isUploadFailed = false
    // 成功分片信息
    let sharding = []

    for (let j = 0; j < chunkCount; j++) {
      const start = j * CHUNK_SIZE
      const end = Math.min(file.files.size, start + CHUNK_SIZE)
      // eslint-disable-next-line no-underscore-dangle
      const _chunkFile = file.files.slice(start, end)
      try {
        // eslint-disable-next-line no-await-in-loop, no-use-before-define
        const getETag = await uploadChunk(
          _chunkFile,
          (chunkUploader as any).UploadId,
          file.fid!,
          file.s3.bucket,
          j + 1,
          s3
        )

        if ((!isContinued && file.show === 1) || (isContinued && file.resetShow === 1)) {
          // 断开连接
          isUploadFailed = true
          file.message = 'message.suspend'
          break
        } else {
          // 成功 将分片信息存起来
          file.percentage = Math.min(99, Math.ceil(file.percentage + 100 / chunkCount))
          sharding.push({ ETag: (getETag as any).ETag, PartNumber: j + 1 })
        }
      } catch (error) {
        // 断开连接
        isUploadFailed = true
        break
      }
    }

    // 向用户输出文件上传失败，并将前端页面改变
    if (isUploadFailed) {
      sharding = []
      file.error = true
      if (file.show === 1) {
        file.message = 'message.suspend'
      } else {
        file.show = 2
        file.message = 'message.failedToUploadShards'
      }
      file.color = STATUS_COLORS.ERROR
      await abortMultipartUpload(file.s3.bucket, file.fid!, (chunkUploader as any).UploadId, s3)
    } else {
      try {
        await completeMultipartUpload(
          file.s3.bucket,
          file.fid!,
          sharding,
          (chunkUploader as any).UploadId,
          s3
        )
        // 5、将合并成功信息传给后端
        try {
          await onPostUploadInfo(file.files.name, file.fid!, file.files.type)
          if (!isContinued) file.isFresh = false
          file.show = 3
          file.color = STATUS_COLORS.SUCCESS
          file.error = false
          file.success = true
          file.message = 'message.uploadSuccessful'
          file.resetShow = 3
          file.percentage = 100
        } catch (error) {
          file.show = 4
          file.error = true
          file.color = STATUS_COLORS.ERROR
          file.message = error as string
        }
      } catch (error) {
        // 失败，将消息返回给页面
        file.show = 2
        file.color = STATUS_COLORS.ERROR
        file.error = true
        file.message = 'message.mergeShardingFailed'
        if (!isContinued) file.isFresh = false
      }
    }
  } catch (error) {
    file.show = 2
    file.error = true
    file.color = STATUS_COLORS.ERROR
    file.message = 'message.failedToUploadShards'
    if (!isContinued) file.isFresh = false
  }
}

// 继续上传的文件列表
const continuedFiles: UploadFile[] = []

// 继续上传
async function onContinueUploading(item: UploadFile) {
  item.s3 = {
    bucket: '',
    endPoint: '',
    accessKeyId: '',
    secretAccessKey: '',
  }
  item.color = STATUS_COLORS.NORMAL
  item.error = false
  item.message = ''
  item.success = false
  item.resetShow = 0
  continuedFiles.push(item)

  try {
    const {
      data: { cognitoResultDTO, bucketName },
    } = await fetchS3InfoAndBucketInfo()

    if (cognitoResultDTO?.token) {
      const s3 = getS3(cognitoResultDTO.identityId, cognitoResultDTO.token)

      while (continuedFiles.length) {
        const errorFile = continuedFiles.shift()

        if (errorFile) {
          errorFile.fid = `${fileOnS3FolderPath.value}${Date.now()}/${errorFile.files.name}`
          errorFile.s3.bucket = bucketName
          if (errorFile.files.size > CHUNK_SIZE) {
            uploadFileWithChunk(errorFile, bucketName, s3, true)
          } else {
            uploadFileWithoutChunk(errorFile, bucketName, s3, true)
          }
        }
      }

      const fileInput: any = document.getElementById('file-input')
      if (fileInput.value) fileInput.value = ''
    }
  } catch (error) {
    ElMessage.error(t('message.failedToEstablishConnection'))
  }
}

// 文件上传
async function onFileChange(event: any) {
  const { files: hostFiles } = event.target
  for (let i = 0; i < hostFiles.length; i++) {
    const extension = hostFiles[i].name.split('.').pop().toLowerCase()

    if (hostFiles[i].size > MAX_FILE_SIZE) {
      ElMessage.warning(t('message.noFileSize'))
      continue
    }
    if (FILE_TYPES.includes(extension)) {
      ElMessage.warning(`${t('message.noUploadBefore')}${extension}${t('message.noUploadType')}`)
      continue
    }

    const item: UploadFile = {
      s3: {
        bucket: '',
        endPoint: '',
        accessKeyId: '',
        secretAccessKey: '',
      },
      fid: '',
      color: STATUS_COLORS.NORMAL,
      error: false,
      filePath: `${state.folders.join('/')}/${hostFiles[i].name}`,
      files: hostFiles[i],
      appear: true,
      success: false,
      message: '',
      isFresh: true,
      show: 0,
      resetShow: 0,
      percentage: 0,
    }

    files.value.push(item)
  }

  try {
    const {
      data: { cognitoResultDTO, bucketName },
    } = await fetchS3InfoAndBucketInfo()

    if (cognitoResultDTO?.token) {
      const s3 = getS3(cognitoResultDTO.identityId, cognitoResultDTO.token)

      for (let i = 0; i < files.value.length; i++) {
        const file = files.value[i]
        if (file.isFresh && file.show !== 1 && !file.fid) {
          file.fid = `${fileOnS3FolderPath.value}${Date.now()}/${file.files.name}`
          file.s3.bucket = bucketName
          if (file.files.size > CHUNK_SIZE) {
            uploadFileWithChunk(file, bucketName, s3)
          } else {
            uploadFileWithoutChunk(file, bucketName, s3)
          }
        }
      }

      const fileInput: any = document.getElementById('file-input')
      if (fileInput) fileInput.value = ''
    }
  } catch (error) {
    ElMessage.error(t('message.failedToEstablishConnection'))
  }
}

const onDragenter = () => {
  if (fileInputRef.value) {
    fileInputRef.value.parentElement!.classList.add('border-blue-300')
    fileInputRef.value.nextElementSibling!.classList.add('opacity-5')
  }
}

const onDragleave = () => {
  if (fileInputRef.value) {
    fileInputRef.value.parentElement!.classList.remove('border-blue-300')
    fileInputRef.value.nextElementSibling!.classList.remove('opacity-5')
  }
}

// 文件弹窗关闭
const onClose = (done: any) => {
  event.publish(PUB_SUB.LIST_STATUS)
  if (fileInputRef.value) fileInputRef.value = null
  done()
}

watchEffect((onCleanup) => {
  event.subscribe(PUB_SUB.FILE, (data: any) => {
    uploaderVisible.value = true
    state.id = data.id
    state.rootId = data.rootId
    state.folders = data.folders
  })
  onCleanup(() => {
    event.unsubscribe(PUB_SUB.FILE)
  })
})

watchEffect((onCleanup) => {
  event.subscribe(PUB_SUB.STOP_FILE, () => {
    files.value.forEach((file: any) => {
      file.show = 1
    })
  })
  onCleanup(() => {
    event.unsubscribe(PUB_SUB.STOP_FILE)
  })
})

watchEffect((onCleanup) => {
  event.subscribe(PUB_SUB.CLEAN_FILE, () => {
    files.value = []
  })
  onCleanup(() => {
    event.unsubscribe(PUB_SUB.CLEAN_FILE)
  })
})

watch(
  () => files.value,
  (n) => {
    const status = n.some((item: any) => !item.error && !item.success)
    event.publish(PUB_SUB.LIST_UPLOAD_STATUS, status)
  },
  { deep: true }
)
</script>

<style lang="scss">
.my-header {
  display: flex;
}

.file-upload-dialog {
  .el-dialog__body {
    @apply pb-0 #{!important};
  }
}

.much-file-content {
  overflow: hidden;

  @apply mx-[-24px] w-[calc(100%+48px)] pb-[24px];

  .file-list-show-box {
    width: 100%;
    margin: 16px 0 0;
    padding: 0 24px;
    max-height: 330px;
    position: relative;
    z-index: 2;
    overflow-y: auto;
  }

  .file-list-li {
    padding-left: 4px;
  }
}
.file-s3-upload {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 2;
}

.file-list-box {
  margin-bottom: 8px;
  .file-list-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .file-list-progress {
    width: 100%;
  }
}
.file-list-status {
  .el-button.is-circle {
    padding: 1px;
    height: auto;
  }
}

.el-link.el-link--default {
  color: #6aaef1;
}
.file-list-li {
  width: 100%;
  display: flex;
  align-items: center;

  @apply py-[6px];

  &:not(:last-child) {
    border-bottom: solid 1px rgba(#dcdfe6, 0.8);
  }
}
.file-list-name {
  margin-bottom: 4px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;

  @apply font-medium leading-[16px] text-[#333];

  .custom-icon {
    margin-right: 6px;
  }
}
.file-li-btn .el-icon {
  margin: 0 3px;
  vertical-align: middle;
  text-align: center;
  display: inline-table;
  width: 10%;
}
.file-drag-box {
  display: flex;
  align-items: center;
  position: relative;
  width: calc(100% - 48px);
  margin: 0 auto;
  border: 2px dashed #dcdfe6;
  border-radius: 5px;

  .file-label {
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    .custom-file-upload {
      cursor: pointer;
      text-align: center;
      .el-icon {
        font-size: 67px;
        color: #a8abb2;
        margin-bottom: 16px;
        line-height: 50px;
      }
      .el-upload__text {
        display: flex;
        em {
          color: #00adef;
          font-style: normal;
          margin-left: 5px;
        }
      }
    }
  }
  .much-file {
    width: 100%;
    height: 210px;
    position: relative;
    z-index: 2;
    cursor: pointer;
    opacity: 0;

    &:active + .file-drag-mask {
      @apply opacity-20;
    }
  }

  .file-drag-mask {
    @apply pointer-events-none absolute top-0 left-0 z-10 h-full w-full bg-black opacity-0;
  }

  &:hover {
    @apply border-blue-300;

    .file-drag-mask {
      @apply opacity-5;
    }
  }

  &:active {
    @apply border-blue-500;

    .file-drag-mask {
      @apply opacity-20;
    }
  }
}
.file-publish-dialog {
  .el-dialog__body {
    padding: 0 24px 28px;
  }
}
</style>
